import { AfterViewInit, Component, ElementRef, HostListener, Input, NO_ERRORS_SCHEMA, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Constants } from '@models/constants';
import { UrlValidationService } from '@services/document/url-validation.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MapfreButtonComponent } from '../../../../../modules/mapfre-design-library/src/lib/_components/individual-components/inputs/mapfre-button/mapfre-button.component';
import { BillingQuestionComponent } from '../../billing/billing-question/billing-question.component';
import { UploadTypeModalComponent } from '../../../../screens/upload-type-modal/upload-type-modal.component';

export interface BillingAssistanceCard {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description: SafeHtml;
  buttonTitle: string;
  actionType: string;
}

@Component({
  standalone: true,
  schemas: [NO_ERRORS_SCHEMA],
  imports: [CommonModule, RouterModule, TranslateModule, MapfreButtonComponent, BillingQuestionComponent, UploadTypeModalComponent],
  selector: 'app-billing-assistance-cards',
  templateUrl: './billing-assistance-cards.component.html',
  styleUrls: ['./billing-assistance-cards.component.scss']
})
export class BillingAssistanceCardsComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() policyNumber: string;
  @Input() company: string
  @Input() isAcmic: boolean;

  @ViewChildren('titleEl') titleEls: QueryList<ElementRef<HTMLDivElement>>;

  maxTitleHeight: number | null = null;

  private resizeTimeout: any;
  private titleElsSub: any;

  cards: BillingAssistanceCard[] = [];

  constructor(
    private router: Router,
    private urlValidationService: UrlValidationService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company'] || changes['isAcmic'] || changes['policyNumber']) {
      this.initCards();
    }
  }

  ngAfterViewInit(): void {
    this.recalculateTitleHeight();
    this.titleElsSub = this.titleEls.changes.subscribe(() => this.recalculateTitleHeight());
  }

  ngOnDestroy(): void {
    if (this.titleElsSub) {
      this.titleElsSub.unsubscribe();
    }
    clearTimeout(this.resizeTimeout);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.recalculateTitleHeight(), 150);
  }

  private recalculateTitleHeight(): void {
    if (!this.titleEls || this.titleEls.length === 0) {
      return;
    }
    setTimeout(() => {
      this.maxTitleHeight = null;
      setTimeout(() => {
        const heights = this.titleEls
          .map((el) => el.nativeElement.getBoundingClientRect().height)
          .filter((h) => !!h);
        if (heights.length) {
          this.maxTitleHeight = Math.max(...heights);
        }
      });
    });
  }

  openInNewTab(url): void {
    if (this.urlValidationService.isApprovedRedirectUrl(url)) {
      window.open(url, '_blank');
    }
  }

  buttonAction(card: BillingAssistanceCard) {
    switch (card.actionType) {
      case 'submitRequest':
        this.router.navigate(['/my-insurance', this.policyNumber, 'billing-request']);
        break;
      case 'goToFaq':
        this.router.navigate(['/faq', 'billing']);
        break;
      case 'easyRepairesLearnMore':
        this.openInNewTab(Constants.EASY_REPAIRES_LEARN_MORE_LINK);
        break;
      case 'stressFreelearnMore':
        this.openInNewTab(Constants.STRESS_FREE_CLAMS_LEARN_MORE_LINK);
        break;
      default:
        break;
    }
  }

  private initCards() {
    if (this.isAcmic) {
      this.cards = [
        {
          imageUrl: 'assets/img/images/billing-assistance-bg.png',
          imageAlt: 'Billing assistance',
          title: 'Billing assistance',
          description: this.sanitizer.bypassSecurityTrustHtml('Get help with your billing statement, making payments, or cancellations.'),
          buttonTitle: 'Send question',
          actionType: 'sendQuestion'
        },
        {
          imageUrl: 'assets/img/images/send-us-bg.png',
          imageAlt: 'Send documents',
          title: 'Send documents',
          description: this.sanitizer.bypassSecurityTrustHtml('Policy, billing, or claim documents can be easily uploaded here.'),
          buttonTitle: 'Upload documents',
          actionType: 'uploadDocuments'
        }
      ];
    } else if (this.company === 'Guidewire') {
      this.cards = [
        {
          imageUrl: 'assets/img/images/billing-assistance-bg.png',
          imageAlt: 'Billing assistance',
          title: 'Billing assistance',
          description: this.sanitizer.bypassSecurityTrustHtml(this.policyNumber
            ? 'Contact us to get help with your billing statement or making payments.'
            : 'Get help with your billing statement, making payments, or cancellations.'),
          buttonTitle: this.policyNumber ? 'Submit request' : 'Ask a question',
          actionType: this.policyNumber ? 'submitRequest' : 'sendQuestion'
        },
        {
          imageUrl: 'assets/img/images/send-us-bg.png',
          imageAlt: 'Send documents',
          title: 'Send documents',
          description: this.sanitizer.bypassSecurityTrustHtml('Policy, billing, or claim documents can be easily uploaded here.'),
          buttonTitle: 'Upload documents',
          actionType: 'uploadDocuments'
        },
        {
          imageUrl: 'assets/img/images/faq-bg.png',
          imageAlt: 'Still have questions?',
          title: 'Still have questions?',
          description: this.sanitizer.bypassSecurityTrustHtml('Get answers to our most frequently asked billing questions.'),
          buttonTitle: 'Go to FAQs',
          actionType: 'goToFaq'
        }
      ];
    } else {
      this.cards = [
        {
          imageUrl: 'assets/img/images/billing-assistance-bg.png',
          imageAlt: 'Billing assistance',
          title: 'Billing assistance',
          description: this.sanitizer.bypassSecurityTrustHtml('Get help with your billing statement, making payments, or cancellations.'),
          buttonTitle: 'Send question',
          actionType: 'sendQuestion'
        },
        {
          imageUrl: 'assets/img/images/easy-repaires-bg.png',
          imageAlt: 'Easy repairs',
          title: 'Easy repairs',
          description: this.sanitizer.bypassSecurityTrustHtml('Repairs through Mapfre\u2019s CAR EZ<sup>\u00ae</sup>-certified partners are guaranteed for as long as you own the vehicle.'),
          buttonTitle: 'Learn more',
          actionType: 'easyRepairesLearnMore'
        },
        {
          imageUrl: 'assets/img/images/stress-free-claims-bg.png',
          imageAlt: 'Stress-free claims',
          title: 'Stress-free claims',
          description: this.sanitizer.bypassSecurityTrustHtml('Expert, guaranteed repairs to your home are provided with the Mapfre Select Home Restoration Program<sup>SM</sup>.'),
          buttonTitle: 'Learn more',
          actionType: 'stressFreelearnMore'
        }
      ];
    }
  }
}
